import qrcode from "qrcode-generator";

/** Light modules the QR specification requires on every side of the symbol (ISO/IEC 18004). */
export const QR_QUIET_ZONE_MODULES = 4;
/** Fewest screen pixels per module that still scans from a monitor. */
export const QR_MIN_CELL_PX = 3;
/** Level M restores the symbol when about 15 % of it is damaged: the usual choice for a screen. */
const QR_ERROR_CORRECTION = "M";
const QR_AUTO_VERSION = 0;
const OUTSIDE_PRINTABLE_ASCII = /[^\x20-\x7e]/gu;
const HEX_RADIX = 16;
const utf8 = new TextEncoder();

export interface QrSymbol {
  /** Modules per side, quiet zone included. */
  extent: number;
  /** SVG path data: one `M x y h w v1 h-w z` rectangle per horizontal run of dark modules. */
  path: string;
}

function percentEncode(char: string): string {
  return Array.from(utf8.encode(char), (byte) => `%${byte.toString(HEX_RADIX).toUpperCase().padStart(2, "0")}`).join("");
}

/** A QR byte payload is read as UTF-8 or Latin-1 depending on the scanner, so anything beyond ASCII is percent-encoded first. */
export function toQrPayload(value: string): string {
  return value.replace(OUTSIDE_PRINTABLE_ASCII, percentEncode);
}

/** Encodes `value` (version chosen automatically, level M) as dark runs inside a quiet zone. */
export function encodeQrSymbol(value: string): QrSymbol {
  const symbol = qrcode(QR_AUTO_VERSION, QR_ERROR_CORRECTION);
  symbol.addData(toQrPayload(value));
  symbol.make();
  const modules = symbol.getModuleCount();
  const runs: string[] = [];
  for (let row = 0; row < modules; row += 1) {
    let column = 0;
    while (column < modules) {
      if (!symbol.isDark(row, column)) {
        column += 1;
        continue;
      }
      const start = column;
      while (column < modules && symbol.isDark(row, column)) column += 1;
      const width = column - start;
      runs.push(`M${String(start + QR_QUIET_ZONE_MODULES)} ${String(row + QR_QUIET_ZONE_MODULES)}h${String(width)}v1h-${String(width)}z`);
    }
  }
  return { extent: modules + 2 * QR_QUIET_ZONE_MODULES, path: runs.join("") };
}

/** Whole pixels per module nearest below `size`, so edges stay crisp when a phone scans a screen. */
export function qrPixelEdge(extent: number, size: number): number {
  return Math.max(QR_MIN_CELL_PX, Math.floor(size / extent)) * extent;
}
